'use client';

import { useContext } from 'react';
import PropTypes from 'prop-types';
import useSWR from 'swr';

import SingleDataValueItem from '../SingleDataValueItem';
import Box from '../Box/Box';
import { fetchFenixDatasetFirstItem } from '../../clients/fenixClient';
import { replaceInheritedValueInFenixFilters } from '../HighCharts/utils';
import { CountryContext } from '../../utils/country-provider';
import {
    formatDescriptionProperty,
    isValueScaleFormat,
} from '../../utils/helpers';

const fetcher = ([dataset, filterParams, orderByProperty, orderByDirection]) =>
    fetchFenixDatasetFirstItem(dataset, filterParams, {
        orderByProperty,
        orderByDirection,
    });

const FenixSingleDataValue = ({
    title = '',
    description: editorialDescription = '',
    unit: editorialUnit = '',
    icon = null,
    border = false,
    dataSource = null,
    property = null,
    filterParams = [],
    decimalPrecision = 0,
    descriptionProperty = null,
    descriptionPropertyFormatting = 'none',
    unitProperty = null,
    valueFormat = 'none',
    orderByProperty = null,
    orderByDirection = 'desc',
}) => {
    const countryMeta = useContext(CountryContext);

    const dataset = dataSource?.value;
    const countryCode = countryMeta?.code;

    // Editor-configured filters may carry the `__inherit_country_code__`
    // placeholder (on either `country_code` or `country_code__ne`); resolve it
    // to the current page's country the same way the published chart renderer
    // does. Without this the unresolved placeholder is sent verbatim and the
    // backend matches nothing.
    const resolvedFilterParams = replaceInheritedValueInFenixFilters(
        filterParams || [],
        countryCode
    );

    // Legacy convenience: when the editor configured no country constraint at
    // all, scope the single value to the current page's country. Skip when a
    // country filter is already present, otherwise we send a duplicate
    // `country_code` param.
    const hasCountryFilter = resolvedFilterParams.some(
        (p) => p.key === 'country_code' || p.key === 'country_code__ne'
    );

    const params = dataset
        ? [
              ...resolvedFilterParams,
              ...(countryCode && !hasCountryFilter
                  ? [{ key: 'country_code', value: countryCode }]
                  : []),
          ]
        : null;

    const swrKey =
        dataset && property
            ? [dataset, params, orderByProperty, orderByDirection]
            : null;

    const { data, error, isLoading } = useSWR(swrKey, fetcher);

    let value = '';
    let description = editorialDescription;
    let unit = editorialUnit;

    if (data?.ok && data.item) {
        const raw = data.item[property];
        if (raw != null) {
            const num = parseFloat(raw);
            value = isNaN(num)
                ? String(raw)
                : num.toFixed(decimalPrecision ?? 0);
        } else {
            value = 'N/A';
        }

        if (descriptionProperty) {
            const rawDesc = data.item[descriptionProperty];
            if (rawDesc != null && rawDesc !== '') {
                const formatted = formatDescriptionProperty(
                    rawDesc,
                    descriptionPropertyFormatting
                );
                description =
                    formatted != null && formatted !== ''
                        ? `(${formatted})`
                        : '';
            } else {
                description = '';
            }
        }

        if (unitProperty) {
            const rawUnit = data.item[unitProperty];
            unit = rawUnit != null ? String(rawUnit) : '';
        }
    }

    const noData = data?.ok && !data.item;

    const isUnitPercentage = unit?.startsWith('%');
    const isUnitLongText = unit?.split(' ').length > 1;
    const cleanUnit = isUnitPercentage ? unit.replace('%', '') : unit;
    const hasValueFormat = isValueScaleFormat(valueFormat);

    if (border) {
        return (
            <Box>
                <SingleDataValueItem
                    icon={icon}
                    title={title}
                    description={description}
                    unit={cleanUnit}
                    valueFormat={valueFormat}
                    decimalPrecision={decimalPrecision}
                    value={value}
                    isLoading={isLoading}
                    errorFetchingData={
                        !!error || (data != null && !data.ok) || noData
                    }
                    isUnitPercentage={!hasValueFormat && isUnitPercentage}
                    isUnitLongText={!hasValueFormat && isUnitLongText}
                />
            </Box>
        );
    }

    return (
        <SingleDataValueItem
            icon={icon}
            title={title}
            description={description}
            unit={cleanUnit}
            valueFormat={valueFormat}
            decimalPrecision={decimalPrecision}
            value={value}
            isLoading={isLoading}
            errorFetchingData={!!error || (data != null && !data.ok) || noData}
            isUnitPercentage={!hasValueFormat && isUnitPercentage}
            isUnitLongText={!hasValueFormat && isUnitLongText}
        />
    );
};

FenixSingleDataValue.propTypes = {
    title: PropTypes.string,
    description: PropTypes.string,
    unit: PropTypes.string,
    icon: PropTypes.string,
    border: PropTypes.bool,
    dataSource: PropTypes.object,
    property: PropTypes.string,
    filterParams: PropTypes.array,
    decimalPrecision: PropTypes.number,
    descriptionProperty: PropTypes.string,
    descriptionPropertyFormatting: PropTypes.string,
    unitProperty: PropTypes.string,
    valueFormat: PropTypes.string,
    orderByProperty: PropTypes.string,
    orderByDirection: PropTypes.string,
};

export default FenixSingleDataValue;
